import { borderRadius, palette, spaces } from '@kimuno/theme';
import {
	Box,
	type SxProps,
	Table,
	TableCell,
	TableContainer,
	TableRow,
	type Theme,
	Typography,
} from '@mui/material';
import type { Components } from 'react-markdown';

const textColor = {
	color: palette.text.primary,
};

/**
 * Default markdown components configuration for ReactMarkdown.
 * Maps markdown elements to MUI components with consistent styling.
 */
export const getMarkdownComponents = (
	imageWidthsByOffset?: Map<number, string>,
	onImageClick?: (src: string, alt?: string) => void
): Components => {
	return {
		h1: ({ node, ref, ...props }) => (
			<Typography variant="h5" component="h1" gutterBottom sx={textColor} {...props} />
		),
		h2: ({ node, ref, ...props }) => (
			<Typography variant="h6" component="h2" gutterBottom sx={textColor} {...props} />
		),
		h3: ({ node, ref, ...props }) => (
			<Typography variant="h6" component="h3" gutterBottom sx={textColor} {...props} />
		),
		h4: ({ node, ref, ...props }) => (
			<Typography variant="h6" component="h4" gutterBottom sx={textColor} {...props} />
		),
		h5: ({ node, ref, ...props }) => (
			<Typography variant="h6" component="h5" gutterBottom sx={textColor} {...props} />
		),
		h6: ({ node, ref, ...props }) => (
			<Typography variant="h6" component="h6" gutterBottom sx={textColor} {...props} />
		),
		p: ({ node, ref, ...props }) => (
			<Typography variant="body1" paragraph sx={textColor} {...props} />
		),
		li: ({ node, ref, ...props }) => (
			<li>
				<Typography
					variant="body1"
					component="div"
					sx={textColor}
					{...(props as React.HTMLAttributes<HTMLDivElement>)}
				/>
			</li>
		),
		table: ({ node, ref, ...props }) => (
			<TableContainer sx={{ marginBottom: spaces.md }}>
				<Table {...props} />
			</TableContainer>
		),
		td: ({ node, ref, ...props }) => <TableCell {...props} align="left" />,
		th: ({ node, ref, ...props }) => <TableCell {...props} align="center" />,
		tr: ({ node, ref, ...props }) => <TableRow {...props} />,
		img: ({ node, ref, style, ...props }) => {
			const clickable = Boolean(onImageClick && props.src);
			const handleClick = clickable
				? () => onImageClick!(props.src as string, props.alt as string | undefined)
				: undefined;

			if (imageWidthsByOffset) {
				const offset = node?.position?.start.offset;
				const width = offset === undefined ? undefined : imageWidthsByOffset.get(offset);
				const mergedStyle = width
					? { ...(style || {}), width, maxWidth: '100%', height: 'auto' }
					: style;
				return (
					<Box
						component="img"
						{...props}
						onClick={handleClick}
						sx={{
							...(mergedStyle as SxProps<Theme>),
							...(clickable ? { cursor: 'pointer' } : {}),
						}}
					/>
				);
			}
			return (
				<Box
					component="img"
					{...props}
					onClick={handleClick}
					sx={{
						maxWidth: 'min(100%, 460px)',
						height: 'auto',
						borderRadius: borderRadius.md,
						...(clickable ? { cursor: 'pointer' } : {}),
						...style,
					}}
				/>
			);
		},
	};
};
